// src/api/apiConfig.js

// ✅ CRA: process.env.REACT_APP_API_BASE_URL
// ✅ Vite: import.meta.env.VITE_API_BASE_URL
function getApiBaseUrl() {
  const viteUrl =
    (typeof import.meta !== "undefined" &&
      import.meta.env &&
      import.meta.env.VITE_API_BASE_URL) ||
    "";
 
  const craUrl =
    (typeof process !== "undefined" &&
      process.env &&
      process.env.REACT_APP_API_BASE_URL) ||
    "";

  // ✅ default fallback
  // const envUrl = (viteUrl || craUrl || "https://starrecs.starmeda.com").trim();
  const envUrl = (viteUrl || craUrl || "http://localhost:3009").trim();


  return envUrl.replace(/\/$/, "");
}

const API_BASE_URL = getApiBaseUrl();

export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

/** ✅ Token getter (prevents "undefined"/"null") */
function getToken() {
  let raw =
    localStorage.getItem("adminToken") ||
    localStorage.getItem("token") ||
    "";

  if (!raw) return "";

  raw = String(raw).trim();

  // remove accidental quotes:  "xxxxx"
  if (
    (raw.startsWith('"') && raw.endsWith('"')) ||
    (raw.startsWith("'") && raw.endsWith("'"))
  ) {
    raw = raw.slice(1, -1).trim();
  }

  if (!raw || raw === "undefined" || raw === "null") return "";

  // ✅ If token is already stored with "Bearer "
  if (raw.toLowerCase().startsWith("bearer ")) {
    raw = raw.slice(7).trim();
  }

  return raw;
}

/** ✅ Hard clear everything auth-related */
function clearAuth() {
  const keys = ["adminToken", "token", "adminMobile", "adminId", "userRole"];
  keys.forEach((k) => localStorage.removeItem(k));

  // ✅ remove any token-like keys (safe cleanup)
  Object.keys(localStorage).forEach((k) => {
    const lk = k.toLowerCase();
    if (lk.includes("token") || lk.includes("auth")) {
      localStorage.removeItem(k);
    }
  });

  try {
    sessionStorage.clear();
  } catch (_) {}

  // ⚠️ optional cookie clear (keep if you want)
  try {
    document.cookie
      .split(";")
      .forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
      });
  } catch (_) {}
}

// ✅ Safely parse response (supports empty body)
async function parseResponse(response) {
  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();

  if (!text) return null;

  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch (_) {
      return text;
    }
  }

  // sometimes backend sends json without header
  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
}

/**
 * ✅ apiRequest(endpoint, options)
 * options:
 *  - method
 *  - params: object -> querystring
 *  - data: object -> json body or FormData
 *  - body: string/FormData -> raw body
 *  - headers
 *  - timeoutMs
 */
export async function apiRequest(endpoint, options = {}) {
  const {
    method = "GET",
    params,
    data,
    body,
    headers: customHeaders = {},
    timeoutMs = 60000,
    ...rest
  } = options;

  const formattedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

  // ✅ Build URL safely
  const urlObj = new URL(`${API_BASE_URL}${formattedEndpoint}`);

  // ✅ Attach query params
  if (params && typeof params === "object") {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        urlObj.searchParams.set(k, String(v));
      }
    });
  }

  const token = getToken();

  const finalHeaders = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...customHeaders,
  };

  let finalBody = body;

  // ✅ JSON body support
  if (data !== undefined) {
    const isFormData =
      typeof FormData !== "undefined" && data instanceof FormData;

    if (isFormData) {
      finalBody = data;
      // ✅ DO NOT set Content-Type for FormData
    } else {
      finalHeaders["Content-Type"] = "application/json";
      finalBody = JSON.stringify(data);
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(urlObj.toString(), {
      method,
      headers: finalHeaders,
      body: method === "GET" || method === "HEAD" ? undefined : finalBody,
      signal: controller.signal,

      // ✅ token based auth
      mode: "cors",
      credentials: "omit",

      ...rest,
    });

    const payload = await parseResponse(response);

    if (!response.ok) {
      if (response.status === 401) {
        clearAuth();
        try {
          if (typeof window !== "undefined") window.location.replace("/");
        } catch (_) {}
      }

      const message =
        typeof payload === "string"
          ? payload
          : payload?.message ||
            payload?.error ||
            `HTTP ${response.status} ${response.statusText}`;

      throw new ApiError(message, response.status, payload);
    }

    return payload;
  } catch (err) {
    if (err?.name === "AbortError") {
      const e = new Error(
        "Request timed out. Server is slow OR preflight (CORS) is blocked."
      );
      e.name = "TimeoutError";
      throw e;
    }

    const msg = (err?.message || "").toLowerCase();
    if (
      msg.includes("failed to fetch") ||
      msg.includes("networkerror") ||
      msg.includes("load failed")
    ) {
      const e = new Error(
        "Network/CORS blocked OR API URL wrong. Check API_BASE_URL and backend CORS (allow Authorization header + OPTIONS)."
      );
      e.name = "NetworkError";
      throw e;
    }

    console.error("API Error:", err?.message || err);
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ✅ Helpers
export const getRequest = (endpoint, params) =>
  apiRequest(endpoint, { method: "GET", params });

export const postRequest = (endpoint, data, params) =>
  apiRequest(endpoint, { method: "POST", data, params });

export const putRequest = (endpoint, data, params) =>
  apiRequest(endpoint, { method: "PUT", data, params });

export const patchRequest = (endpoint, data, params) =>
  apiRequest(endpoint, { method: "PATCH", data, params });

export const deleteRequest = (endpoint, data, params) =>
  apiRequest(endpoint, { method: "DELETE", data, params });

// ✅ Auth API (admin)
export const authAPI = {
  sendOTP: (mobile) => postRequest("/business/auth/login", { mobile }),
  verifyOTP: (mobile, otp) => postRequest("/business/auth/verify", { mobile, otp }),
  getCurrentUser: () => getRequest("/business/auth/me"),

  logout: () => clearAuth(),

  logoutAndRedirect: (navigate, to = "/") => {
    clearAuth();
    try {
      if (navigate) navigate(to, { replace: true });
    } catch (_) {}
    try {
      window.location.replace(to);
    } catch (_) {}
  },

  isAuthenticated: () => !!getToken(),
  getToken: () => getToken(),

  saveAuthData: (data) => {
    if (data?.token) {
      // store raw token only (no Bearer)
      const t = String(data.token).replace(/^Bearer\s+/i, "").trim();
      localStorage.setItem("adminToken", t);
      localStorage.setItem("token", t);
    }
    if (data?.user?._id) localStorage.setItem("adminId", data.user._id);
    if (data?.user?.mobile) localStorage.setItem("adminMobile", data.user.mobile);
    localStorage.setItem("userRole", "admin");
  },
};
